// Right-hand reference panel for the panel board editor: RCD groups and lines
// (circuits) derived from the same BoardLayout the canvas renders.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Circuit, RCDGroup } from '@/types/api';
import { formatCircuitLabel, formatLoadCategory, formatRcdGroupLabel } from '@/lib/electricalLabels';
import type { HighlightSet } from './grouping';
import type { CircuitGroupMoveBlockReason, RcdGroupDeleteBlockReason } from './editLayout';

interface GroupsLinesPanelProps {
  groups: readonly RCDGroup[];
  circuits: readonly Circuit[];
  highlight: HighlightSet;
  onFocusGroup: (groupId: string) => void;
  onFocusCircuit: (circuitId: string) => void;
  getCircuitGroupMoveBlockReason: (circuitId: string, targetGroupId: string) => CircuitGroupMoveBlockReason | null;
  onMoveCircuitToGroup: (circuitId: string, targetGroupId: string) => void;
  getRcdGroupDeleteBlockReason: (groupId: string) => RcdGroupDeleteBlockReason | null;
  onDeleteRcdGroup: (groupId: string) => void;
}

// Card border/background reflect highlight state. Highlight is interaction-only:
// the .pb-highlight marker lets the print stylesheet neutralize the accent so the
// card still prints, just without the on-screen selection tint.
function cardClass(active: boolean): string {
  return (
    'w-full text-left rounded border p-3 text-xs transition-colors ' +
    (active
      ? 'pb-highlight border-accent bg-accent/10'
      : 'border-border bg-surface hover:bg-surface-3')
  );
}

export function GroupsLinesPanel({
  groups,
  circuits,
  highlight,
  onFocusGroup,
  onFocusCircuit,
  getCircuitGroupMoveBlockReason,
  onMoveCircuitToGroup,
  getRcdGroupDeleteBlockReason,
  onDeleteRcdGroup,
}: GroupsLinesPanelProps) {
  const { t } = useTranslation();
  const [editingGroups, setEditingGroups] = useState(false);
  const [draggingCircuitId, setDraggingCircuitId] = useState<string | null>(null);
  const circuitById = new Map(circuits.map((circuit) => [circuit.circuit_id, circuit]));

  const canDropCircuitOnGroup = (circuitId: string | null, targetGroupId: string): boolean => {
    if (!circuitId) return false;
    const targetGroup = groups.find((group) => group.group_id === targetGroupId);
    if (!targetGroup || targetGroup.circuit_ids.includes(circuitId)) return false;
    return getCircuitGroupMoveBlockReason(circuitId, targetGroupId) !== 'c13_dedicated';
  };

  return (
    <aside className="flex flex-col gap-3">
      <section>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="font-sans text-sm font-semibold text-ink-3">
            {t('panel_board.groups')} ({groups.length})
          </h3>
          <button
            type="button"
            onClick={() => setEditingGroups((value) => !value)}
            className={
              'no-print rounded border px-2 py-1 font-mono text-xs transition-colors ' +
              (editingGroups
                ? 'border-accent bg-accent/10 text-accent'
                : 'border-border text-ink-3 hover:bg-surface-3')
            }
          >
            {editingGroups ? t('panel_board.finish_group_edit') : t('panel_board.edit_groups')}
          </button>
        </div>
        {editingGroups && (
          <p className="no-print mb-3 text-xs leading-relaxed text-ink-4">
            {t('panel_board.group_edit_hint')}
          </p>
        )}
        {groups.length === 0 ? (
          <div className="text-xs text-ink-4">{t('panel_board.no_groups')}</div>
        ) : (
          <div className="space-y-3">
            {groups.map((group, groupIndex) => {
              const groupCircuits = group.circuit_ids
                .map((circuitId) => circuitById.get(circuitId))
                .filter((circuit): circuit is Circuit => Boolean(circuit));
              const canDeleteEmptyRcd = getRcdGroupDeleteBlockReason(group.group_id) === null;
              const canDropHere = editingGroups && canDropCircuitOnGroup(draggingCircuitId, group.group_id);
              return (
                <div
                  key={group.group_id}
                  onDragOver={(event) => {
                    if (!canDropHere) return;
                    event.preventDefault();
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (!canDropHere || !draggingCircuitId) return;
                    onMoveCircuitToGroup(draggingCircuitId, group.group_id);
                    setDraggingCircuitId(null);
                  }}
                  className={
                    cardClass(highlight.groupIds.has(group.group_id)) +
                    (canDropHere ? ' ring-1 ring-accent/50' : '')
                  }
                >
                  <button
                    type="button"
                    onClick={() => onFocusGroup(group.group_id)}
                    className="block w-full text-left"
                  >
                    <div className="font-sans text-xs font-medium text-ink">
                      {formatRcdGroupLabel(t, groupIndex)}
                    </div>
                    <div className="mt-1 text-ink-3">
                      {group.protection_device_id}
                      {' · '}
                      {t('electrical.circuit_count', { count: group.circuit_ids.length })}
                    </div>
                  </button>
                  <div className="mt-3 space-y-2">
                    {groupCircuits.length === 0 ? (
                      <div className="rounded border border-dashed border-border px-2 py-2 text-xs text-ink-4">
                        {t('panel_board.no_lines_in_group')}
                      </div>
                    ) : (
                      groupCircuits.map((circuit) => {
                        const canMoveSomewhere = groups.some((target) =>
                          target.group_id !== group.group_id &&
                          getCircuitGroupMoveBlockReason(circuit.circuit_id, target.group_id) !== 'c13_dedicated',
                        );
                        return (
	                          <div
	                            key={circuit.circuit_id}
                              draggable={editingGroups && canMoveSomewhere}
                              onDragStart={(event) => {
                                if (!editingGroups || !canMoveSomewhere) return;
                                setDraggingCircuitId(circuit.circuit_id);
                                event.dataTransfer.effectAllowed = 'move';
                                event.dataTransfer.setData('text/plain', circuit.circuit_id);
                              }}
                              onDragEnd={() => setDraggingCircuitId(null)}
	                            className={
	                              'rounded border px-2 py-2 transition-colors ' +
	                              (highlight.circuitIds.has(circuit.circuit_id)
	                                ? 'pb-highlight border-accent bg-accent/10'
	                                : 'border-border bg-surface-2') +
                                (editingGroups && canMoveSomewhere ? ' cursor-grab active:cursor-grabbing' : '') +
                                (draggingCircuitId === circuit.circuit_id ? ' opacity-50' : '')
	                            }
	                          >
                            <button
                              type="button"
                              onClick={() => onFocusCircuit(circuit.circuit_id)}
                              className="block w-full text-left"
                            >
                              <div className="font-sans text-xs font-medium text-ink">
                                {formatCircuitLabel(t, circuit)}
                              </div>
                              <div className="mt-1 text-[11px] text-ink-3">
                                {formatLoadCategory(t, circuit.category)}
                                {' · '}
                                {circuit.protection_device_id}
                              </div>
                            </button>
	                            {editingGroups && !canMoveSomewhere && (
	                              <div className="no-print mt-2 font-mono text-[11px] text-ink-4">
	                                {t('panel_board.c13_group_locked')}
	                              </div>
	                            )}
	                          </div>
                        );
                      })
                    )}
                    {editingGroups && groupCircuits.length === 0 && (
                      <div className="no-print rounded border border-danger/30 bg-danger/5 p-2">
                        <div className="text-xs font-medium text-danger">
                          {t('panel_board.delete_empty_rcd_title')}
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-ink-3">
                          {t('panel_board.delete_empty_rcd_explain')}
                        </p>
                        <div className="mt-2">
                          <button
                            type="button"
                            disabled={!canDeleteEmptyRcd}
                            onClick={() => onDeleteRcdGroup(group.group_id)}
                            className="rounded border border-danger/40 bg-danger/10 px-2 py-1 font-mono text-xs text-danger transition-colors hover:bg-danger/15 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {t('panel_board.delete_empty_rcd')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </aside>
  );
}
